import { memo, useMemo, useRef, useState } from "react";
import SearchInput from "../../../shared/SearchInput";
import GenericTableHeader, {
  tableCellStyle,
} from "../../../shared/TableHeader";
import { Box, HStack } from "../../../shared/utils";
import { AgGridReact } from "ag-grid-react";
import { ButtonSize, IconCTAButton } from "../../../shared/CTAButtonComponents";
import EmptyTableIllustration from "../../../shared/EmptyTableIllustration";
import { agConfigDashboardSideBar } from "../../../shared/AgGridConfig";
import { ColDef } from "ag-grid-community";
import { formatDate } from "../../../utils/date";
import {
  CompanyNameRender,
  DateOfSharedRender,
  PageNameRender,
} from "./SharedTableAgComponents";
import { useAuthStore } from "../../../store/useAuthStore";

type SharedWithMeProps = {
  data: any[];
};

export default function SharedWithMeAgTable(props: SharedWithMeProps) {
  const sharedData = props.data || [];
  const gridApi = useRef<any>(null);
  const { accessToken } = useAuthStore();
  const [isColumnOpen, setIsColumnOpen] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const componentsRegistery = useMemo(
    () => ({
      dateOfShared: memo(DateOfSharedRender),
      resourceType: memo(PageNameRender),
      companyName: memo(CompanyNameRender),
    }),
    []
  );

  const gridOptions = {
    suppressRowVirtualisation: true,
    paginationAutoPageSize: false,
    suppressScrollOnNewData: true,
  };

  const showNoRowsOverlay = () => (
    <EmptyTableIllustration text={`${"No Board meeting details found"}`} />
  );

  const [filteredRowData, setFilteredRowData] = useState<any>([]);
  const [isFilterApplied, setIsFilterApplied] = useState(false);

  const onAgGridFilterChanged = (grid: any) => {
    const filtersApplied = grid.api.isAnyFilterPresent();
    setIsFilterApplied(filtersApplied);
    const filteredData = grid.api
      .getModel()
      .rowsToDisplay.map((node: any) => node.data);
    setFilteredRowData(filteredData);
    // setPinnedBottomRowData(grid);
    // uploadFilter();
  };

  const openToolPanel = (key: any) => {
    if (key === "columns") {
      if (gridApi) {
        if (!isColumnOpen) gridApi?.current?.api?.openToolPanel(key);
        else gridApi?.current?.api?.closeToolPanel();
        setIsColumnOpen((state) => !state);
        setIsFilterOpen(false);
      }
    } else if (key === "filters") {
      if (gridApi) {
        if (!isFilterOpen) {
          gridApi?.current?.api?.openToolPanel(key);
        } else gridApi?.current?.api?.closeToolPanel();
        setIsFilterOpen((state) => !state);
        setIsColumnOpen(false);
      }
    }
  };

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      autoHeight: true,
      wrapHeaderText: true,
      suppressColumnVirtualisation: true,
      columnsMenuParams: {
        suppressColumnFilter: true,
      },
      filterParams: {
        buttons: ["reset"],
        maxNumConditions: 5,
      },
      minWidth: 150,
      filter: true,
      resizable: true,
      flex: 1,
    }),
    []
  );

  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: "Shared Resource",
        field: "resourceType",
        filter: "agSetColumnFilter",
        cellRenderer: "resourceType",
        cellStyle: tableCellStyle,
        sortable: true,
        menuTabs: ["filterMenuTab"],
        // pinned: "left",
        initialWidth: 400,
        autoHeight: true,
        wrapText: true,
        suppressSizeToFit: true,
        tooltipField: "resourceTypeTooltip",
      },
      {
        headerName: "Platform",
        field: "platform",
        filter: "agSetColumnFilter",
        cellRenderer: "platform",
        cellStyle: tableCellStyle,
        sortable: true,
        menuTabs: ["filterMenuTab"],
        autoHeight: true,
        wrapText: true,
        suppressSizeToFit: true,
      },
      {
        headerName: "Company Name",
        field: "companyName",
        filter: "agSetColumnFilter",
        cellRenderer: "companyName",
        cellStyle: tableCellStyle,
        sortable: true,
        minWidth: 300,
        menuTabs: ["filterMenuTab"],
      },
      // {
      //   headerName: "Shared By",
      //   field: "sharedBy",
      //   filter: "agSetColumnFilter",
      //   cellRenderer: "sharedBy",
      //   cellStyle: tableCellStyle,
      //   sortable: true,
      //   menuTabs: ["filterMenuTab"],
      // },
      {
        headerName: "Shared Date",
        field: "dateOfShared",
        filter: "agDateColumnFilter",
        filterParams: {
          comparator: (dateFromFilter: Date, cellValue: any) => {
            if (cellValue == null) {
              return 0;
            }
            // const cellDateValue = cellValue.split('T')[0];
            const dateParts = cellValue.split("-");
            const day = Number(dateParts[2]);
            const month = Number(dateParts[1]) - 1;
            const year = Number(dateParts[0]);
            const cellDate = new Date(year, month, day);
            if (cellDate < dateFromFilter) {
              return -1;
            } else if (cellDate > dateFromFilter) {
              return 1;
            }
            return 0;
          },
        },
        cellRenderer: "dateOfShared",
        cellStyle: tableCellStyle,
        sortable: true,
        menuTabs: ["filterMenuTab"],
      },
      // {
      //   headerName: "Tags",
      //   field: "tags",
      //   filter: "agTextColumnFilter",
      //   cellRenderer: "tags",
      //   cellStyle: tableCellStyle,
      //   sortable: true,
      //   hide: true,
      //   menuTabs: ["filterMenuTab"],
      // },
      // {
      //   headerName: "Notes",
      //   field: "notes",
      //   filter: "agTextColumnFilter",
      //   cellRenderer: "notes",
      //   cellStyle: tableCellStyle,
      //   sortable: true,
      //   hide: true,
      //   menuTabs: ["filterMenuTab"],
      // },
      // {
      //   headerName: "",
      //   field: "actions",
      //   pinned: "right",
      //   hide: false,
      //   width: 80,
      //   maxWidth: 80,
      //   filter: false,
      //   menuTabs: [],
      //   colId: "action-column",
      //   suppressNavigable: true,
      //   suppressColumnsToolPanel: true,
      //   cellStyle: { "padding-top": "12px", "line-height": "18px" },
      //   resizable: false,
      //   sortable: false,
      //   cellRendererParams: ({ value }: { value: any }) => value?.props,
      //   cellRenderer: CTADropdown,
      // },
    ],
    [sharedData]
  );

  const rowData = useMemo(
    () =>
      sharedData.map((data) => {
        return {
          resourceType: data.resourceType,
          resourceId: data.resourceId,
          platform: data.platform,
          // sharedBy: data.sharedBy,
          companyName: data.companyName,
          dateOfShared: formatDate(data.createdAt),
          currentRowData: data,
          accessToken:accessToken,
          // tags: data.tags,
          // notes: data.notes,
          // actions: <CTADropdown value={data} />,
        };
      }),
    [sharedData]
  );

  return (
    <>
      <div className="border border-gray-200 mt-6 rounded-lg">
        <HStack className="flex-col lg:flex-row justify-start lg:justify-between  py-2 lg:items-center items-start">
          {/* <div>
            <GenericTableHeader
              heading={"Shared With Me"}
              subHeading={`${sharedData.length} Pages`}
            />
          </div> */}
          <div></div>
          <HStack className="flex flex-col lg:flex-row items-center justify-between w-full gap-4 px-8 py-2 lg:w-auto lg:justify-end">
            <SearchInput
              placeholder={`Search`}
              onChange={(e: any) => {
                gridApi.current.api.setQuickFilter(e.target.value);
              }}
            />

            <HStack className="gap-4">
              <IconCTAButton
                value={"Columns"}
                onClick={() => openToolPanel("columns")}
                buttonSize={ButtonSize.SMALL}
                iconName={"fluent:column-triple-edit-20-regular"}
                className={`px-4 py-2 font-medium items-center flex flex-row ${
                  isColumnOpen ? "text-primary" : "text-gray-400"
                }`}
                selected={isColumnOpen}
              />
              <IconCTAButton
                value={"Filters"}
                buttonSize={ButtonSize.SMALL}
                onClick={() => openToolPanel("filters")}
                iconName={"material-symbols:filter-alt"}
                className={`px-4 py-2 font-medium items-center flex flex-row ${
                  isFilterOpen ? "text-orange-501" : "text-gray-400"
                }`}
                selected={isFilterOpen}
              />
            </HStack>
          </HStack>
        </HStack>
        {/* <Dialog open={dialog.open} maxWidth="md">
          {(dialog.name === "Edit" || dialog.name === "Add") && (
            <AddEditMeetings
              directorData={props.directorData}
              meetings={dialog.data}
              onSecondaryAction={() => {
                setDialog({ open: false });
              }}
              mode={dialog.name}
            />
          )}
          {dialog.name === "Delete" && (
            <AlertDialog
              onClose={() => setDialog({ open: false })}
              open={dialog.open}
              message={"Do you want to delete this Meeting Details?"}
              onPrimaryAction={() => {
                deleteMeetingDetails({
                  id: dialog.data.id,
                  companyId: dialog.data.companyId,
                });
                setDialog({ open: false });
              }}
              onSecondaryAction={() => setDialog({ open: false })}
              primaryActionText="Delete"
            />
          )}
          {dialog.name === "Documents" && (
            <DocumentsPopup
              id={dialog?.data || ""}
              onPrimaryAction={() => {
                setDialog({
                  open: true,
                  name: "Documents",
                  data: dialog.data,
                });
              }}
              onSecondaryAction={() => {
                setDialog({
                  open: false,
                  name: "Documents",
                });
              }}
              type="meeting"
            />
          )}
        </Dialog> */}
        <HStack className="justify-between w-full">
          <Box
            style={{
              height: "400px",
            }}
            className="w-full h-full max-h-full overflow-x-auto ag-theme-material "
          >
            <AgGridReact
              sideBar={agConfigDashboardSideBar}
              onGridReady={(params) => {
                // Store the grid API referen
                gridApi.current = params;
              }}
              components={componentsRegistery}
              alwaysShowHorizontalScroll
              alwaysMultiSort
              animateRows={true}
              defaultColDef={defaultColDef}
              onFilterChanged={onAgGridFilterChanged}
              // onColumnEverythingChanged={uploadColumn}
              rowData={rowData}
              // onCellClicked={handleCellClick}
              columnDefs={columnDefs}
              pagination={false}
              suppressRowTransform={true}
              suppressCopyRowsToClipboard={true}
              suppressCopySingleCellRanges={true}
              suppressCellFocus={true}
              suppressMenuHide={false}
              // onColumnResized={uploadColumn}
              noRowsOverlayComponent={showNoRowsOverlay}
              tooltipShowDelay={1000}
              tooltipInteraction={true}
              rowClass={"border-t border-dashed"}
              gridOptions={gridOptions}
              overlayNoRowsTemplate={`<span style="padding: 6px; border: 2px solid #444; background: lightgoldenrodyellow; margin-top: 50px; border-radius: 5px;">${"No details found"}</span>`}
              getRowStyle={(params) => {
                if (params.rowIndex % 2 === 0) {
                  return { background: "#f8f8f8" };
                } else {
                  return { background: "#ffffff" };
                }
              }}
            ></AgGridReact>
          </Box>
        </HStack>
      </div>
    </>
  );
}
